import { useCallback, useEffect, useRef, useState } from 'react';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';
import { HighlightPositionsData } from '@mygpt/app/chat/services';
import { useElementSize } from '@mantine/hooks';

const PageFooter = ({ pageNum }: { pageNum: number }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center py-2">
      <div className="flex-1 border-dotted border-t-2 border-gray-200"></div>
      <span className="px-3 text-white text-xs">{t('pdf_viewer.page_of', { page: pageNum })}</span>
      <div className="flex-1 border-dotted border-t-2 border-gray-200"></div>
    </div>
  );
};

export const Page = ({
  pdf,
  currentPage,
  highlightPositions,
  fitWidth = false,
}: {
  pdf: PDFDocumentProxy;
  currentPage: number;
  highlightPositions?: HighlightPositionsData['positions'][number];
  fitWidth?: boolean;
}) => {
  const pdfCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const highlightCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const { ref: pageContainerRef, width: containerWidth } = useElementSize();
  const fitWidthContainerWidth = fitWidth ? containerWidth : 0;
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [renderScale, setRenderScale] = useState(1);
  const [highlightContext, setHighlightContext] = useState<CanvasRenderingContext2D | null>();

  // 渲染PDF内容到底层canvas
  useEffect(() => {
    if (fitWidth && fitWidthContainerWidth <= 0) return;

    let renderTask: { cancel: () => void } | undefined;
    let cancelled = false;

    pdf.getPage(currentPage).then((page) => {
      if (cancelled) return;

      const baseViewport = page.getViewport({ scale: 1 });
      if (!pdfCanvasRef.current) return;

      const scale = fitWidth ? Math.max(1, Math.floor(fitWidthContainerWidth)) / baseViewport.width : 1;
      const viewport = page.getViewport({ scale });

      const context = pdfCanvasRef.current.getContext('2d');
      if (!context) {
        console.error('获取PDF canvas context失败');
        return;
      }

      // 设置canvas尺寸
      const width = viewport.width;
      const height = viewport.height;
      setDimensions({ width, height });
      setRenderScale(scale);

      pdfCanvasRef.current.width = width;
      pdfCanvasRef.current.height = height;

      // 渲染PDF内容
      renderTask = page.render({
        canvasContext: context,
        viewport,
        canvas: pdfCanvasRef.current,
      });
      void renderTask.promise.catch((error) => {
        if (error?.name !== 'RenderingCancelledException') {
          console.error('渲染PDF页面失败', error);
        }
      });
    });

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, currentPage, fitWidth, fitWidthContainerWidth]);

  // 设置高亮canvas
  useEffect(() => {
    if (highlightCanvasRef.current && dimensions.width > 0) {
      // 设置高亮canvas尺寸
      highlightCanvasRef.current.width = dimensions.width;
      highlightCanvasRef.current.height = dimensions.height;

      const context = highlightCanvasRef.current.getContext('2d');
      setHighlightContext(context);
    }
  }, [dimensions]);

  // 高亮渲染逻辑
  const renderHighlights = useCallback(
    (context: CanvasRenderingContext2D, matches: HighlightPositionsData['positions'][number]['matches']) => {
      if (!matches || matches.length === 0) return;

      // 设置高亮样式
      context.globalAlpha = 0.5;
      context.fillStyle = 'yellow';

      // 绘制高亮区域
      matches.forEach((match) => {
        const x = match[1] * renderScale;
        const y = match[2] * renderScale;
        const width = (match[3] - match[1]) * renderScale;
        const height = (match[4] - match[2]) * renderScale;

        context.fillRect(x, y, width, height);
      });

      // 重置透明度
      context.globalAlpha = 1.0;
    },
    [renderScale]
  );

  // 处理高亮渲染
  useEffect(() => {
    if (!highlightContext) return;

    // 总是先清除旧的高亮内容
    highlightContext.clearRect(0, 0, dimensions.width, dimensions.height);

    // 如果有新的高亮数据，则渲染新高亮
    if (highlightPositions) {
      renderHighlights(highlightContext, highlightPositions.matches);
    }
  }, [highlightContext, highlightPositions, renderHighlights, dimensions]);

  return (
    <div ref={pageContainerRef} className={`${fitWidth ? 'w-full' : 'w-fit'} m-auto relative`}>
      {/* PDF渲染层 - 底层 */}
      <canvas ref={pdfCanvasRef} className="bg-white block" />

      {/* 高亮层 - 顶层 */}
      <canvas
        ref={highlightCanvasRef}
        className="absolute top-0 left-0 pointer-events-none"
        style={{
          width: dimensions.width,
          height: dimensions.height,
          mixBlendMode: 'multiply',
        }}
      />

      <PageFooter pageNum={currentPage} />
    </div>
  );
};
